// Сметы: список + импорт из Excel + AI-проверка.
import { useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Upload, Sparkles, FileDown, Plus } from 'lucide-react';
import { estimatesApi, contractsApi, exportApi, estimateEditorApi, getErrorMessage } from '@/api';
import { AppLayout, PageHeader } from '@/components/layout/AppLayout';
import { RichSidebar } from '@/components/layout/RichSidebar';
import { Spinner } from '@/components/ui/Spinner';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { EmptyState } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/Toast';
import { useAuth } from '@/hooks/useAuth';
import { formatMoney } from '@/lib/format';

const STATUS_COLORS: Record<string, string> = {
  draft: 'var(--text-muted)', review: 'var(--yellow)', approved: 'var(--green)', rejected: 'var(--red)',
};

export function EstimatesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const statusFilter = searchParams.get('status') || undefined;
  const [createOpen, setCreateOpen] = useState(false);
  const { can } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [importOpen, setImportOpen] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ['estimates', { status: statusFilter }], queryFn: () => estimatesApi.list(statusFilter ? { status: statusFilter } : {}) });

  const aiCheck = useMutation({
    mutationFn: (id: number) => estimatesApi.aiCheck(id),
    onSuccess: (r) => toast(r.task_id ? 'AI-проверка запущена' : `Проверка (${r.provider}) выполнена`, 'success'),
    onError: (e) => toast(getErrorMessage(e), 'error'),
  });

  return (
    <AppLayout sidebar={<RichSidebar />}>
      <PageHeader title={statusFilter === 'review' ? 'Проверка смет' : 'Сметы'} subtitle={data ? `${data.count} смет` : undefined}
        actions={can('estimates', 'add') && (
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={() => setImportOpen(true)}><Upload size={16} /> Импорт из Excel</Button>
            <Button variant="primary" onClick={() => setCreateOpen(true)}><Plus size={16} /> Создать смету</Button>
          </div>
        )} />
      <div className="panel">
        {isLoading ? <Spinner label="Загрузка смет…" /> :
          !data || data.results.length === 0 ? (
            <EmptyState title="Смет пока нет" hint="Импортируйте смету из Excel или Гранд-Сметы" />
          ) : (
            <table className="data-table">
              <thead>
                <tr><th>Номер</th><th>Вид</th><th>Договор</th><th>Позиций</th><th>Статус</th><th style={{ textAlign: 'right' }}>Итого</th><th></th></tr>
              </thead>
              <tbody>
                {data.results.map((e) => (
                  <tr key={e.id} onClick={() => navigate(`/estimates/${e.id}`)} style={{ cursor: 'pointer' }}>
                    <td className="mono">{e.number}</td>
                    <td>{e.kind_display}</td>
                    <td>{e.contract_number}</td>
                    <td className="mono">{e.lines_count}</td>
                    <td><StatusBadge label={e.status_display} color={STATUS_COLORS[e.status] || 'var(--text-muted)'} /></td>
                    <td style={{ textAlign: 'right' }} className="mono">{formatMoney(e.total)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Button size="sm" variant="ghost" disabled={aiCheck.isPending}
                          onClick={(ev) => { ev.stopPropagation(); aiCheck.mutate(e.id); }}>
                          <Sparkles size={14} /> AI
                        </Button>
                        <Button size="sm" variant="ghost" title="Экспорт в Excel"
                          onClick={(ev) => { ev.stopPropagation(); exportApi.estimate(e.id, e.number); }}>
                          <FileDown size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>
      <CreateEstimateModal open={createOpen} onClose={() => setCreateOpen(false)}
        onCreated={(id) => { qc.invalidateQueries({ queryKey: ['estimates'] }); toast('Смета создана', 'success'); navigate(`/estimates/${id}`); }}
        onError={(m) => toast(m, 'error')} />
      <ImportEstimateModal open={importOpen} onClose={() => setImportOpen(false)}
        onDone={(imported, total) => { qc.invalidateQueries({ queryKey: ['estimates'] }); toast(`Импортировано позиций: ${imported} на ${formatMoney(total)}`, 'success'); }}
        onError={(m) => toast(m, 'error')} />
    </AppLayout>
  );
}

function ImportEstimateModal({ open, onClose, onDone, onError }: {
  open: boolean; onClose: () => void; onDone: (imported: number, total: string) => void; onError: (msg: string) => void;
}) {
  const [contract, setContract] = useState<number | ''>('');
  const [number, setNumber] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const { data: contracts } = useQuery({ queryKey: ['contracts', {}], queryFn: () => contractsApi.list(), enabled: open });
  const mutation = useMutation({
    mutationFn: () => estimatesApi.importExcel(file!, Number(contract), number),
    onSuccess: (r) => { onClose(); setFile(null); onDone(r.imported, r.total); },
    onError: (e) => onError(getErrorMessage(e)),
  });
  return (
    <Modal open={open} onClose={onClose} title="Импорт сметы из Excel"
      footer={<>
        <Button onClick={onClose}>Отмена</Button>
        <Button variant="primary" disabled={!file || !contract || !number || mutation.isPending} onClick={() => mutation.mutate()}>
          {mutation.isPending ? 'Импорт…' : 'Импортировать'}
        </Button>
      </>}>
      <div className="field">
        <label className="field__label">Договор</label>
        <select className="field__select" value={contract} onChange={(e) => setContract(e.target.value ? Number(e.target.value) : '')}>
          <option value="">— выберите —</option>
          {contracts?.results.map((c) => <option key={c.id} value={c.id}>{c.number}</option>)}
        </select>
      </div>
      <div className="field">
        <label className="field__label">Номер сметы</label>
        <input className="field__input" value={number} onChange={(e) => setNumber(e.target.value)} placeholder="ЛСР-01" />
      </div>
      <div className="field">
        <label className="field__label">Файл Excel (формат ФЕР, 12 колонок)</label>
        <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        <Button style={{ width: '100%' }} onClick={() => fileRef.current?.click()}>{file ? file.name : 'Выбрать файл…'}</Button>
      </div>
    </Modal>
  );
}

function CreateEstimateModal({ open, onClose, onCreated, onError }: {
  open: boolean; onClose: () => void; onCreated: (id: number) => void; onError: (m: string) => void;
}) {
  const [number, setNumber] = useState('');
  const [contract, setContract] = useState<number | ''>('');
  const [normBase, setNormBase] = useState('fer_2024');
  const { data: contracts } = useQuery({ queryKey: ['contracts-all'], queryFn: () => contractsApi.list({}), enabled: open });
  const m = useMutation({
    mutationFn: () => estimateEditorApi.create({ number, contract: Number(contract), norm_base: normBase }),
    onSuccess: (est) => { onClose(); setNumber(''); setContract(''); onCreated(est.id); },
    onError: (e) => onError(getErrorMessage(e)),
  });
  return (
    <Modal open={open} onClose={onClose} title="Новая смета (с нуля)"
      footer={<>
        <Button onClick={onClose}>Отмена</Button>
        <Button variant="primary" disabled={!number || !contract || m.isPending} onClick={() => m.mutate()}>
          {m.isPending ? 'Создание…' : 'Создать и редактировать'}
        </Button>
      </>}>
      <div className="field"><label className="field__label">Номер сметы</label>
        <input className="field__input" value={number} onChange={(e) => setNumber(e.target.value)} placeholder="ЛС-2026-001" /></div>
      <div className="field"><label className="field__label">Договор</label>
        <select className="field__select" value={contract} onChange={(e) => setContract(e.target.value ? Number(e.target.value) : '')}>
          <option value="">— выберите —</option>
          {contracts?.results.map((c) => <option key={c.id} value={c.id}>{c.number}</option>)}
        </select></div>
      <div className="field"><label className="field__label">Нормативная база</label>
        <select className="field__select" value={normBase} onChange={(e) => setNormBase(e.target.value)}>
          <option value="fer_2024">ФЕР 2024</option>
          <option value="fer_2020">ФЕР 2020</option>
          <option value="gesn">ГЭСН</option>
        </select></div>
    </Modal>
  );
}
